import React, {
  createContext, useCallback, useEffect, useMemo, useState,
} from 'react';
import { node } from 'prop-types';
import { notification } from 'antd';
import client from '../ApolloClient';
import { GET_ME } from '../graphql';
import { clearSession, getToken, saveSession } from '../auth/session';

export const AuthContext = createContext({});

/**
 * Who is signed in.
 *
 * The previous version read a cached user record out of localStorage and
 * asked the API for *that id* — which is the client half of a query any
 * caller could point at any account. The record now comes from `me`, which
 * takes no argument, so the only account this can load is the one the token
 * belongs to.
 */
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authStateLoading, setAuthStateLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);

  const revokeAuth = useCallback(() => {
    clearSession();
    setIsAuthenticated(false);
    setUserInfo(null);
    // Otherwise the next sign-in reads the previous user's cached results.
    client.clearStore();
  }, []);

  const grantAuth = useCallback((token, user) => {
    saveSession(token);
    setUserInfo(user);
    setIsAuthenticated(true);
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!getToken()) {
        if (!cancelled) setAuthStateLoading(false);
        return;
      }
      try {
        const { data } = await client.query({ query: GET_ME, fetchPolicy: 'network-only' });
        if (cancelled) return;
        setUserInfo(data.me);
        setIsAuthenticated(true);
      } catch {
        if (cancelled) return;
        revokeAuth();
        notification.info({
          message: 'Account Login',
          description: 'Please login with your username and password',
        });
      } finally {
        if (!cancelled) setAuthStateLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [revokeAuth]);

  const value = useMemo(() => ({
    isAuthenticated,
    userInfo,
    authStateLoading,
    grantAuth,
    revokeAuth,
  }), [isAuthenticated, userInfo, authStateLoading, grantAuth, revokeAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: node.isRequired,
};
